import React, { useState } from 'react';
import classNames from 'classnames';
import { useAsyncEffect, useDidUpdate } from 'rooks';
import { Icon } from '@seeqdev/qomponents';
import { UnitOfMeasure } from '@/core/UnitOfMeasure.atom';
import { onEnterKeypress } from '@/core/onEnterKeypress.util';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import { Checkbox } from '@/core/Checkbox.atom';
import { isAsset, isDatafile, isDisplay, isHierarchy, isTableDefinition, itemIconClass } from '@/utilities/utilities';
import { sqAssetGroupStore, sqItemTableEditorStore, sqScalingTableEditorStore } from '@/core/core.stores';
import { onEventExceptDisplayDropdown } from '@/displays/displays.utilities';
import { clearSelection, toggleSelectItem } from '@/search/search.actions';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import type { AnyProperty } from '@/utilities.types';
import { extractDisplayUnitFromIncludedProperties, resolveUnitOfMeasure } from '@/utilities/unit.utilities';
import type { SelectedSearchItem } from '@/search/search.types';
import { getItemTableTypes, getScalingTableTypes } from '@/tableDefinitionEditor/tableDefinition.utilities';
import { HierarchyNodeOutputV1 } from '@/sdk';
import { HierarchyNodeType } from '@/search/search.constants';

const CONDITION_TYPES = [
  SeeqNames.Types.Condition,
  SeeqNames.Types.StoredCondition,
  SeeqNames.Types.CalculatedCondition,
] as const;

export interface SearchResultProps {
  searchResultIcons: (item: SelectedSearchItem) => Promise<React.ReactNode>;
  item: any;
  items?: any[];
  customIconClasses?: string;
  onClickItem: (item: AnyProperty) => void;
  showItemSelection?: boolean;
  isSelectingAsset?: boolean;
  searchTypes?: string[];
  itemSelected?: boolean;
  isSelected?: boolean;
  isAllUnselectable?: boolean;
  highlightItem?: boolean;
  onSelectItem?: (item: any) => void;
  assetGroupEditorVisible?: boolean;
  showOnlyConditions?: boolean;
  doNotDisableOnEdit?: boolean;
  noNavigate?: boolean;
}

export const SearchResult: React.FunctionComponent<SearchResultProps> = ({
  searchResultIcons,
  item,
  items,
  onClickItem,
  customIconClasses,
  onSelectItem,
  isSelectingAsset = false,
  searchTypes,
  itemSelected = false,
  isSelected = false,
  isAllUnselectable = false,
  showItemSelection = false,
  highlightItem = false,
  assetGroupEditorVisible = false,
  showOnlyConditions = false,
  doNotDisableOnEdit = false,
  noNavigate = false,
}) => {
  const itemIconClassNames = `${customIconClasses ? customIconClasses : 'sq-fairly-dark-gray'} pl5 pr10`;
  const arrowIconClassNames = `${customIconClasses ? customIconClasses : 'text-interactive'} mr5`;
  const isEditingAssetGroup = useFluxPath(sqAssetGroupStore, () => sqAssetGroupStore.id === item.id);
  const isEditingTableDefinition = useFluxPath(
    sqScalingTableEditorStore,
    () => sqScalingTableEditorStore.tableId === item.id,
  );
  const isEditingItemTable = useFluxPath(sqItemTableEditorStore, () => sqItemTableEditorStore.tableId === item.id);
  const tableTypeProp = item.includedProperties?.[SeeqNames.Properties.TableType]?.value;

  const isScalingTableThatCanBeNavigated =
    isTableDefinition(item) && getScalingTableTypes().includes(tableTypeProp) && !doNotDisableOnEdit;
  const isItemTableThatCanBeNavigated = isTableDefinition(item) && getItemTableTypes().includes(tableTypeProp);
  const isNavigableHierarchy = isHierarchy(item);
  const isHierarchyNode = item.type === HierarchyNodeType && !!item.node;
  const node = isHierarchyNode ? (item.node as HierarchyNodeOutputV1) : undefined;
  const type = node?.itemMetadata?.type || item.type;
  const isPartOfHierarchyAndHasChildren = isHierarchyNode && !item.node.leaf;
  const showNavigateCaret =
    isAsset(item) ||
    isDatafile(item) ||
    isScalingTableThatCanBeNavigated ||
    isItemTableThatCanBeNavigated ||
    isNavigableHierarchy ||
    isPartOfHierarchyAndHasChildren ||
    item?.hasActiveChildren ||
    (item.ancestors?.length === 1 &&
      item.ancestors?.some((ancestor: any) => ancestor.type === SeeqNames.Types.TableDefinition));
  const resolvedUom = resolveUnitOfMeasure(extractDisplayUnitFromIncludedProperties(item));

  const showIcons =
    (!isSelectingAsset && !searchTypes) ||
    (searchTypes && !searchTypes.includes(SeeqNames.Types.Asset) && searchTypes.includes(type));

  const [isItemSelected, setSelected] = useState(false);
  const [icons, setIcons] = useState<React.ReactNode>(null);

  useDidUpdate(() => setSelected(itemSelected), [itemSelected]);

  useAsyncEffect(async () => {
    const newIcons = await searchResultIcons(item);
    setIcons(newIcons);
  }, [searchResultIcons, item]);

  const unfocusSearchResult = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    (e.target as HTMLElement).blur();
  };

  const isAssetOrAssetNode = isAsset(item) || isAsset(node?.itemMetadata) || type === HierarchyNodeType;

  const renderCheckbox = () => {
    if (showItemSelection && (!isAllUnselectable || assetGroupEditorVisible)) {
      return (
        <Checkbox
          id={`select_${item.id}`}
          label=""
          classes={classNames('ml5 mr5', {
            invisible:
              isTableDefinition(item) ||
              (!assetGroupEditorVisible && (isAssetOrAssetNode || isDisplay(item) || item?.hasActiveChildren)),
          })}
          isChecked={isItemSelected}
          onClick={() => {
            toggleSelectItem(item.id, items);
            setSelected((isSelected) => !isSelected);
          }}
          flexAlign="center"
        />
      );
    }

    return <></>;
  };

  const isEditing = isEditingAssetGroup || isEditingTableDefinition || isEditingItemTable;
  const searchResult = (
    <div
      data-testid={icons ? 'searchResultWithIcons' : 'searchResult'}
      tabIndex={0}
      onMouseLeave={unfocusSearchResult}
      onClick={() => {
        const isOnlySelectingAssets = searchTypes?.includes(SeeqNames.Types.Asset) && searchTypes.length === 1;
        if (
          (!item.hasActiveChildren && !isOnlySelectingAssets) ||
          (isOnlySelectingAssets && !item.hasActiveChildren && isAsset(item)) ||
          doNotDisableOnEdit
        ) {
          onSelectItem?.(item);
        }
      }}
      className={classNames(
        'flexColumnContainer',
        'itemRow',
        'flexNoGrowNoShrink',
        'cursorPointer',
        {
          'disabledItemRow text-italic  disabledLook cursorNotAllowed': isEditing,
          'searchResultWithIcons': !!icons,
        },
        { itemRowSelected: isSelected },
        { highlightFade: highlightItem },
      )}>
      {renderCheckbox()}

      <div
        data-testid="searchResultItem"
        className="flexColumnContainer flexSpaceBetween flexFill searchResultItem pt5 pb5"
        onKeyUp={onEnterKeypress(() => !isEditingAssetGroup && onClickItem(item))}
        onClick={onEventExceptDisplayDropdown(() => {
          if (!isEditingAssetGroup) {
            onClickItem(item);
            clearSelection();
          }
        })}>
        <div className="flexColumnContainer flexCenter">
          {isSelectingAsset && icons}
          <Icon
            icon={itemIconClass(item)}
            testId="searchItemIcon"
            type="inherit"
            extraClassNames={itemIconClassNames}
            large={true}
          />
        </div>

        <div className="aggressiveWordBreak flexFill flexRowContainer flexJustifyCenter">
          <div className="searchResultName">
            <span>{item.name} </span>
            <UnitOfMeasure unitOfMeasure={resolvedUom} />
          </div>
          {item.description && (
            <div className="xsmall pb5" data-testid="searchResultDescription">
              {item.description}
            </div>
          )}
          {(item.assets?.[0]?.formattedName || item?.datasource?.name) && (
            <div className="xsmall pb3 sq-fairly-dark-gray text-italic">
              {item.assets?.[0]?.formattedName || item?.datasource?.name}
            </div>
          )}
        </div>

        <div className="flexRowContainer flexCenter">
          <div data-testid="selectOrNavigate" className="flexColumnContainer flexCenter">
            {showIcons && icons}

            {showNavigateCaret && !noNavigate && (
              <Icon icon="fa-chevron-right" type="inherit" extraClassNames={arrowIconClassNames} testId="arrowIcon" />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const getSearchResult = () =>
    showOnlyConditions && !(CONDITION_TYPES.includes(type) || item.hasActiveChildren) ? undefined : searchResult;

  return (
    <HoverTooltip text={isEditingAssetGroup ? 'SEARCH_DATA.NO_ACTIONS_WHILE_EDITING' : ''}>
      {getSearchResult()}
    </HoverTooltip>
  );
};
